import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  ReactFlow,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ConnectionMode,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
  Handle,
  Position,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { streamChat } from '../services/api';

// ─── Interview stage types ────────────────────────────────────────────────────

const STAGES = [
  'Clarifying Questions',
  'Requirements',
  'High-Level Design',
  'Data Design',
  'Deep Dive',
  'Scaling & Bottlenecks',
  'Tradeoffs',
] as const;

type InterviewStage = (typeof STAGES)[number];

type StageFeedback = {
  stage: InterviewStage;
  score: number;
  strengths: string[];
  weaknesses: string[];
  missed_points: string[];
  next_action: 'advance' | 'follow_up';
};

type FinalEvaluation = {
  final_score: number;
  level: 'weak' | 'borderline' | 'strong hire';
  stage_scores: Record<string, number>;
  key_feedback: string[];
  biggest_gaps: string[];
  recommendations: string[];
};

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
};

// ─── Diagram node types ───────────────────────────────────────────────────────

type NodeKind = 'client' | 'service' | 'database' | 'cache' | 'queue' | 'cdn' | 'loadbalancer' | 'storage' | 'note';

const NODE_META: Record<NodeKind, { label: string; icon: string; color: string }> = {
  client:       { label: 'Client',        icon: 'devices',       color: '#3b82f6' },
  loadbalancer: { label: 'Load Balancer', icon: 'balance',       color: '#6366f1' },
  service:      { label: 'Service',       icon: 'api',           color: '#10b981' },
  database:     { label: 'Database',      icon: 'database',      color: '#f59e0b' },
  cache:        { label: 'Cache',         icon: 'bolt',          color: '#f97316' },
  queue:        { label: 'Queue',         icon: 'queue',         color: '#a855f7' },
  cdn:          { label: 'CDN',           icon: 'public',        color: '#14b8a6' },
  storage:      { label: 'Storage',       icon: 'folder_open',   color: '#ef4444' },
  note:         { label: 'Note',          icon: 'sticky_note_2', color: '#ca8a04' },
};

type DiagramNodeData = {
  label: string;
  kind: NodeKind;
};

function DiagramNode({ id, data, selected }: NodeProps & { data: DiagramNodeData }) {
  const { updateNodeData } = useReactFlow();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const meta = NODE_META[data.kind];

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  return (
    <div
      onDoubleClick={() => setEditing(true)}
      style={{
        background: 'rgb(var(--color-surface-container))',
        borderColor: selected ? meta.color : `${meta.color}66`,
        borderWidth: selected ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: 10,
        minWidth: 110,
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        cursor: editing ? 'text' : 'grab',
        boxShadow: selected ? `0 0 0 3px ${meta.color}22` : 'none',
      }}
    >
      <Handle id="top"    type="source" position={Position.Top}    style={{ background: meta.color, width: 8, height: 8 }} />
      <Handle id="left"   type="source" position={Position.Left}   style={{ background: meta.color, width: 8, height: 8 }} />
      <span className="material-symbols-outlined" style={{ fontSize: 20, color: meta.color }}>{meta.icon}</span>
      {editing ? (
        <input
          ref={inputRef}
          value={data.label}
          onChange={(e) => updateNodeData(id, { label: e.target.value })}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false); }}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 11,
            color: 'rgb(var(--color-on-surface))',
            fontWeight: 500,
            textAlign: 'center',
            width: 90,
          }}
        />
      ) : (
        <span style={{ fontSize: 11, color: 'rgb(var(--color-on-surface))', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>
          {data.label}
        </span>
      )}
      <Handle id="right"  type="source" position={Position.Right}  style={{ background: meta.color, width: 8, height: 8 }} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={{ background: meta.color, width: 8, height: 8 }} />
    </div>
  );
}

function NoteNode({ id, data, selected }: NodeProps & { data: DiagramNodeData }) {
  const { updateNodeData } = useReactFlow();
  return (
    <div
      style={{
        background: 'rgb(var(--color-surface-container))',
        borderColor: selected ? '#ca8a04' : '#ca8a0455',
        borderWidth: selected ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: 8,
        minWidth: 140,
        minHeight: 70,
        padding: '8px 10px',
        cursor: 'grab',
        boxShadow: selected ? '0 0 0 3px #ca8a0422' : 'none',
      }}
    >
      <Handle id="top"    type="source" position={Position.Top}    style={{ background: '#ca8a04', width: 8, height: 8 }} />
      <Handle id="left"   type="source" position={Position.Left}   style={{ background: '#ca8a04', width: 8, height: 8 }} />
      <textarea
        value={data.label}
        onChange={(e) => updateNodeData(id, { label: e.target.value })}
        placeholder="Type your note…"
        rows={3}
        style={{
          background: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'none',
          fontSize: 11,
          color: 'rgb(var(--color-on-surface))',
          width: '100%',
          lineHeight: 1.5,
          cursor: 'text',
          fontFamily: 'inherit',
        }}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <Handle id="right"  type="source" position={Position.Right}  style={{ background: '#ca8a04', width: 8, height: 8 }} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={{ background: '#ca8a04', width: 8, height: 8 }} />
    </div>
  );
}

const nodeTypes = {
  diagram: DiagramNode,
  note: NoteNode,
};

// ─── Diagram serialization for LLM ───────────────────────────────────────────

function serializeDiagram(nodes: Node[], edges: Edge[]): string {
  if (nodes.length === 0) return '';
  const nodeMap: Record<string, string> = {};
  const components: string[] = [];
  for (const n of nodes) {
    const d = n.data as DiagramNodeData;
    nodeMap[n.id] = d.label;
    components.push(`${d.label} (${NODE_META[d.kind].label})`);
  }
  const connections = edges
    .map((e) => `${nodeMap[e.source] ?? e.source} → ${nodeMap[e.target] ?? e.target}`)
    .join(', ');
  return `[Architecture Diagram] Components: ${components.join(', ')}${connections ? `. Connections: ${connections}` : ''}`;
}

// ─── Diagram Canvas ───────────────────────────────────────────────────────────

const PALETTE: NodeKind[] = ['client', 'loadbalancer', 'service', 'database', 'cache', 'queue', 'cdn', 'storage', 'note'];

function DiagramCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onAddNode,
  onNodeDragStart,
}: {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: ReturnType<typeof useNodesState>[2];
  onEdgesChange: ReturnType<typeof useEdgesState>[2];
  onConnect: (c: Connection) => void;
  onAddNode: (kind: NodeKind) => void;
  onNodeDragStart: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Node palette */}
      <div className="flex items-center gap-1.5 px-3 h-12 bg-surface-container-low border-b border-outline-variant/10 shrink-0 overflow-x-auto">
        {PALETTE.map((kind) => {
          const meta = NODE_META[kind];
          return (
            <button
              key={kind}
              onClick={() => onAddNode(kind)}
              title={`Add ${meta.label}`}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium shrink-0 hover:opacity-80 transition-opacity"
              style={{ borderColor: `${meta.color}55`, background: 'rgb(var(--color-surface-container))', color: meta.color }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{meta.icon}</span>
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* React Flow canvas */}
      <div className="flex-1 bg-surface-container-lowest">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStart={onNodeDragStart}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={12}
          fitView
          deleteKeyCode="Backspace"
          style={{ background: 'rgb(var(--color-surface-container-lowest))' }}
          defaultEdgeOptions={{
            style: { stroke: 'rgb(var(--color-outline-variant))', strokeWidth: 1.5 },
            markerEnd: { type: 'arrowclosed' as const, color: 'rgb(var(--color-outline-variant))' },
          }}
        >
          <Background color="rgb(var(--color-outline-variant))" variant={BackgroundVariant.Dots} gap={20} size={1} />
        </ReactFlow>
      </div>
    </div>
  );
}

// ─── Session persistence ──────────────────────────────────────────────────────

type PersistedSession = {
  conversationId: string | undefined;
  stageIndex: number;
  messages: Message[];
  finalEval: FinalEvaluation | null;
  nodes: Node[];
  edges: Edge[];
  savedAt: string;
};

type ChatState = Pick<PersistedSession, 'conversationId' | 'stageIndex' | 'messages' | 'finalEval'> & { started: boolean };

function sessionKey(type: string) {
  return `architect_session_${type}`;
}

function loadSession(type: string): PersistedSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(type));
    return raw ? (JSON.parse(raw) as PersistedSession) : null;
  } catch {
    return null;
  }
}

function clearSession(type: string) {
  localStorage.removeItem(sessionKey(type));
}

// ─── Interview helpers ────────────────────────────────────────────────────────

function extractFirstJson(text: string): { json: Record<string, unknown>; start: number; end: number } | null {
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') {
      depth--;
      if (depth === 0) {
        try {
          return { json: JSON.parse(text.slice(start, i + 1)) as Record<string, unknown>, start, end: i };
        } catch { return null; }
      }
    }
  }
  return null;
}

function stripAllJson(text: string): string {
  let result = text;
  // Strip fenced blocks first
  result = result.replace(/```json[\s\S]*?```/g, '');
  // Strip bare JSON objects
  let safety = 0;
  while (safety++ < 10) {
    const found = extractFirstJson(result);
    if (!found) break;
    result = (result.slice(0, found.start) + result.slice(found.end + 1)).trim();
  }
  return result.trim();
}

function parseAIResponse(content: string): {
  feedback: StageFeedback | FinalEvaluation | null;
  narrative: string;
} {
  // Try fenced block first
  const fencedMatch = content.match(/```json\s*([\s\S]*?)```/);
  if (fencedMatch) {
    try {
      const json = JSON.parse(fencedMatch[1]) as StageFeedback | FinalEvaluation;
      return { feedback: json, narrative: stripAllJson(content) };
    } catch { /* fall through */ }
  }

  // Try bare JSON object
  const found = extractFirstJson(content);
  if (found) {
    return {
      feedback: found.json as StageFeedback | FinalEvaluation,
      narrative: stripAllJson(content),
    };
  }

  return { feedback: null, narrative: content };
}

function isFinalEval(f: StageFeedback | FinalEvaluation): f is FinalEvaluation {
  return 'final_score' in f;
}

function scoreColor(score: number) {
  if (score >= 7) return 'text-emerald-400';
  if (score >= 4) return 'text-yellow-400';
  return 'text-red-400';
}

function scoreBg(score: number) {
  if (score >= 7) return 'bg-emerald-400/10 border-emerald-400/30';
  if (score >= 4) return 'bg-yellow-400/10 border-yellow-400/30';
  return 'bg-red-400/10 border-red-400/30';
}

function levelColor(level: FinalEvaluation['level']) {
  if (level === 'strong hire') return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30';
  if (level === 'borderline') return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
  return 'text-red-400 bg-red-400/10 border-red-400/30';
}

// ─── Stage Progress Bar ───────────────────────────────────────────────────────

function StageProgressBar({ stageIndex }: { stageIndex: number }) {
  return (
    <div className="shrink-0 px-3 py-2 border-b border-outline-variant/10 bg-surface-container-low">
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
        {STAGES.map((stage, i) => {
          const isCompleted = i < stageIndex;
          const isActive = i === stageIndex;
          return (
            <div
              key={stage}
              className={`flex items-center gap-1 shrink-0 px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                isActive
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : isCompleted
                    ? 'bg-surface-container text-on-surface-variant/50'
                    : 'text-on-surface-variant/30'
              }`}
            >
              {isCompleted && (
                <span className="material-symbols-outlined text-emerald-400" style={{ fontSize: 10 }}>check_circle</span>
              )}
              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
              <span>{stage}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Feedback Card ────────────────────────────────────────────────────────────

const FEEDBACK_SECTIONS = [
  { key: 'strengths' as const,     label: 'Strengths', icon: '+', color: 'text-emerald-400', summary: (n: number) => `${n} strength${n !== 1 ? 's' : ''}` },
  { key: 'weaknesses' as const,    label: 'Weaknesses', icon: '−', color: 'text-red-400',    summary: (n: number) => `${n} weakness${n !== 1 ? 'es' : ''}` },
  { key: 'missed_points' as const, label: 'Missed',    icon: '!', color: 'text-yellow-400', summary: (n: number) => `${n} missed` },
] as const;

function FeedbackCard({ feedback }: { feedback: StageFeedback }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`mx-4 mb-3 rounded-xl border p-3 text-xs ${scoreBg(feedback.score)}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-on-surface-variant font-medium uppercase tracking-wider" style={{ fontSize: 9 }}>
          Stage Evaluation · {feedback.stage}
        </span>
        <div className="flex items-center gap-2">
          <span className={`font-bold text-sm ${scoreColor(feedback.score)}`}>{feedback.score}/10</span>
          <button onClick={() => setExpanded((v) => !v)} className="text-on-surface-variant hover:text-on-surface transition-colors">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{expanded ? 'expand_less' : 'expand_more'}</span>
          </button>
        </div>
      </div>
      {expanded ? (
        <div className="space-y-2">
          {FEEDBACK_SECTIONS.map(({ key, label, icon, color }) =>
            feedback[key].length > 0 ? (
              <div key={key}>
                <p className={`${color} font-medium mb-0.5`}>{label}</p>
                <ul className="space-y-0.5">
                  {feedback[key].map((s, i) => (
                    <li key={i} className="text-on-surface-variant flex gap-1">
                      <span className={`shrink-0 ${color}`}>{icon}</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </div>
      ) : (
        <div className="flex gap-3 text-on-surface-variant/60">
          {FEEDBACK_SECTIONS.map(({ key, color, summary }) =>
            feedback[key].length > 0 ? (
              <span key={key} className={`${color}/70`}>{summary(feedback[key].length)}</span>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

// ─── Final Evaluation Panel ───────────────────────────────────────────────────

function FinalEvaluationPanel({ eval: ev, onReset }: { eval: FinalEvaluation; onReset: () => void }) {
  const [openSection, setOpenSection] = useState<string | null>('key_feedback');
  const toggle = (s: string) => setOpenSection((v) => (v === s ? null : s));
  const sections = [
    { key: 'key_feedback', label: 'Key Feedback', items: ev.key_feedback, color: 'text-primary' },
    { key: 'biggest_gaps', label: 'Biggest Gaps', items: ev.biggest_gaps, color: 'text-red-400' },
    { key: 'recommendations', label: 'Recommendations', items: ev.recommendations, color: 'text-emerald-400' },
  ];
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
      <div className="text-center space-y-3 pt-2">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold capitalize ${levelColor(ev.level)}`}>
          {ev.level === 'strong hire' && <span className="material-symbols-outlined" style={{ fontSize: 16 }}>verified</span>}
          {ev.level === 'borderline' && <span className="material-symbols-outlined" style={{ fontSize: 16 }}>help</span>}
          {ev.level === 'weak' && <span className="material-symbols-outlined" style={{ fontSize: 16 }}>cancel</span>}
          {ev.level}
        </div>
        <div>
          <p className={`text-4xl font-bold ${scoreColor(ev.final_score)}`}>{ev.final_score}<span className="text-lg text-on-surface-variant font-normal">/10</span></p>
          <p className="text-xs text-on-surface-variant mt-1">Final Score</p>
        </div>
      </div>
      <div className="bg-surface-container rounded-xl p-3 space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-on-surface-variant font-medium mb-2">Stage Breakdown</p>
        {Object.entries(ev.stage_scores).map(([stage, score]) => (
          <div key={stage} className="flex items-center gap-2">
            <span className="text-xs text-on-surface-variant flex-1 truncate">{stage}</span>
            <div className="flex-1 h-1.5 bg-surface-container-high rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${score >= 7 ? 'bg-emerald-400' : score >= 4 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${score * 10}%` }} />
            </div>
            <span className={`text-xs font-medium w-6 text-right ${scoreColor(score)}`}>{score}</span>
          </div>
        ))}
      </div>
      {sections.map((s) => (
        <div key={s.key} className="bg-surface-container rounded-xl overflow-hidden">
          <button onClick={() => toggle(s.key)} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-surface-container-high transition-colors">
            <span className={`text-xs font-medium ${s.color}`}>{s.label}</span>
            <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: 16 }}>{openSection === s.key ? 'expand_less' : 'expand_more'}</span>
          </button>
          {openSection === s.key && (
            <ul className="px-3 pb-3 space-y-1.5">
              {s.items.map((item, i) => <li key={i} className="text-xs text-on-surface-variant flex gap-2"><span className={`shrink-0 ${s.color}`}>•</span>{item}</li>)}
            </ul>
          )}
        </div>
      ))}
      <button onClick={onReset} className="w-full py-2.5 rounded-xl border border-outline-variant/20 text-sm text-on-surface-variant hover:text-on-surface hover:border-outline-variant/40 transition-colors">
        Start New Interview
      </button>
    </div>
  );
}

// ─── Shared constants ─────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SpeechRecognitionAPI: new () => any =
  typeof window !== 'undefined'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? ((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition)
    : null;

const MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-1 last:mb-0 text-sm">{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold text-on-surface">{children}</strong>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="text-sm">{children}</li>,
  code: ({ children }: { children?: React.ReactNode }) => <code className="bg-surface-container-high px-1 rounded text-xs font-mono text-primary">{children}</code>,
};

// ─── Chat Panel ───────────────────────────────────────────────────────────────

function ChatPanel({
  diagramNodes,
  diagramEdges,
  interviewType,
  initialState,
  onStateChange,
  onResetSession,
}: {
  diagramNodes: Node[];
  diagramEdges: Edge[];
  interviewType: string;
  initialState?: PersistedSession | null;
  onStateChange: (s: ChatState) => void;
  onResetSession: () => void;
}) {
  const [messages, setMessages] = useState<Message[]>(initialState?.messages ?? []);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(initialState?.conversationId);
  const [started, setStarted] = useState(!!initialState);
  const [stageIndex, setStageIndex] = useState(initialState?.stageIndex ?? 0);
  const [latestFeedback, setLatestFeedback] = useState<StageFeedback | null>(null);
  const [finalEval, setFinalEval] = useState<FinalEvaluation | null>(initialState?.finalEval ?? null);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<{ stop: () => void } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const latestAssistantIdRef = useRef<string | null>(null);

  const toggleMic = useCallback(() => {
    if (!SpeechRecognitionAPI) return;
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition = new SpeechRecognitionAPI() as any;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (e: any) => {
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final) setInput((prev) => (prev ? prev + ' ' : '') + final.trim());
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  useEffect(() => () => { recognitionRef.current?.stop(); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, latestFeedback, finalEval]);

  // Report state to parent for persistence
  useEffect(() => {
    onStateChange({ messages, conversationId, stageIndex, finalEval, started });
  }, [messages, conversationId, stageIndex, finalEval, started, onStateChange]);

  const handleReset = () => {
    onResetSession();
    setMessages([]);
    setInput('');
    setIsStreaming(false);
    setConversationId(undefined);
    setStarted(false);
    setStageIndex(0);
    setLatestFeedback(null);
    setFinalEval(null);
    latestAssistantIdRef.current = null;
  };

  const sendMessage = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || isStreaming) return;

      setLatestFeedback(null);

      // Append diagram context if the board has content
      const diagramContext = serializeDiagram(diagramNodes, diagramEdges);
      const fullContent = diagramContext ? `${content}\n\n${diagramContext}` : content;

      const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content };
      const assistantId = crypto.randomUUID();
      latestAssistantIdRef.current = assistantId;
      const assistantMsg: Message = { id: assistantId, role: 'assistant', content: '', isStreaming: true };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setInput('');
      setIsStreaming(true);

      const history = messages
        .filter((m) => !m.isStreaming)
        .map((m) => ({ role: m.role, content: m.content }));

      let fullResponse = '';

      await streamChat(
        fullContent,
        history,
        (chunk) => {
          fullResponse += chunk;
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m)),
          );
        },
        () => {
          const { feedback, narrative } = parseAIResponse(fullResponse);
          if (feedback) {
            if (isFinalEval(feedback)) {
              setFinalEval(feedback);
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: narrative || '', isStreaming: false } : m)),
              );
            } else {
              setLatestFeedback(feedback);
              if (feedback.next_action === 'advance') {
                setStageIndex((prev) => Math.min(prev + 1, STAGES.length - 1));
              }
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: narrative, isStreaming: false } : m)),
              );
            }
          } else {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)),
            );
          }
          setIsStreaming(false);
          inputRef.current?.focus();
        },
        (error) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: error || 'Failed to connect to AI service', isStreaming: false }
                : m,
            ),
          );
          setIsStreaming(false);
        },
        conversationId,
        (id) => setConversationId(id),
        undefined,
        'system-design',
        interviewType,
      );
    },
    [messages, isStreaming, conversationId, diagramNodes, diagramEdges, interviewType],
  );

  const handleStart = () => {
    setStarted(true);
    void sendMessage(`I'm ready for a system design interview. Please give me a problem to design.`);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">

      {/* Pre-start screen */}
      {!started && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
          <div className="text-center space-y-2 max-w-xs">
            <div className="w-14 h-14 rounded-xl primary-gradient flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-on-primary text-2xl">architecture</span>
            </div>
            <h2 className="text-lg font-bold text-on-surface">AI System Design Interview</h2>
            <p className="text-sm text-on-surface-variant">
              A structured, stage-gated interview. Complete each stage before advancing. Build your architecture on the canvas — it's shared with the AI automatically.
            </p>
          </div>
          <button
            onClick={handleStart}
            className="px-8 py-2.5 rounded-lg primary-gradient text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Start Interview
          </button>
        </div>
      )}

      {/* Final evaluation */}
      {started && finalEval && (
        <>
          <div className="shrink-0 px-4 py-2 border-b border-outline-variant/10 bg-surface-container-low">
            <p className="text-xs text-on-surface-variant text-center font-medium">Interview Complete</p>
          </div>
          <FinalEvaluationPanel eval={finalEval} onReset={handleReset} />
        </>
      )}

      {/* Active interview */}
      {started && !finalEval && (
        <>
          <StageProgressBar stageIndex={stageIndex} />

          {/* Messages */}
          <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-0">
            {messages.map((m, idx) => {
              const isLatestAssistant = m.role === 'assistant' && m.id === latestAssistantIdRef.current;
              const showFeedback = isLatestAssistant && !m.isStreaming && latestFeedback !== null;
              return (
                <div key={m.id}>
                  <div className={`flex gap-2 px-4 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {m.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full primary-gradient flex items-center justify-center shrink-0 mt-0.5">
                        <span className="material-symbols-outlined text-on-primary shrink-0" style={{ fontSize: 14 }}>auto_awesome</span>
                      </div>
                    )}
                    <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-primary/20 text-on-surface rounded-br-sm'
                        : 'bg-surface-container text-on-surface rounded-bl-sm'
                    }`}>
                      {m.role === 'assistant' && m.isStreaming ? (
                        <span className="flex items-center gap-1 py-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </span>
                      ) : m.role === 'assistant' ? (
                        <ReactMarkdown components={MD_COMPONENTS}>
                          {m.content}
                        </ReactMarkdown>
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                  {showFeedback && idx === messages.length - 1 && (
                    <div className="mt-2">
                      <FeedbackCard feedback={latestFeedback!} />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 px-4 py-3 border-t border-outline-variant/10">
            <div className="flex items-center gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendMessage(input);
                  }
                }}
                disabled={isStreaming}
                placeholder={`Respond to the ${STAGES[stageIndex]} stage…`}
                rows={2}
                className="flex-1 input-field text-sm resize-none"
              />
              {SpeechRecognitionAPI && (
                <button
                  onClick={toggleMic}
                  disabled={isStreaming}
                  title={isListening ? 'Stop recording' : 'Dictate'}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all shrink-0 disabled:opacity-40 ${
                    isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-surface-container text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    {isListening ? 'mic' : 'mic_none'}
                  </span>
                </button>
              )}
              <button
                onClick={() => void sendMessage(input)}
                disabled={isStreaming || !input.trim()}
                className="w-10 h-10 flex items-center justify-center rounded-lg primary-gradient text-on-primary disabled:opacity-40 transition-opacity shrink-0"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SystemDesignInterview() {
  const navigate = useNavigate();
  const { type: interviewType = 'ai-system-design' } = useParams<{ type: string }>();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Session persistence
  const [savedSession] = useState<PersistedSession | null>(() => loadSession(interviewType));
  const [sessionResolved, setSessionResolved] = useState(!savedSession);
  const [activeSession, setActiveSession] = useState<PersistedSession | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStateChange = useCallback((s: ChatState) => {
    if (!s.started) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const session: PersistedSession = {
        ...s,
        nodes: nodesRef.current,
        edges: edgesRef.current,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(sessionKey(interviewType), JSON.stringify(session));
    }, 800);
  }, [interviewType]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  const handleResume = () => {
    if (savedSession) {
      setNodes(savedSession.nodes);
      setEdges(savedSession.edges);
    }
    setActiveSession(savedSession);
    setSessionResolved(true);
  };

  const handleStartFresh = () => {
    clearSession(interviewType);
    setActiveSession(null);
    setSessionResolved(true);
  };

  const handleResetSession = useCallback(() => {
    clearSession(interviewType);
    setActiveSession(null);
  }, [interviewType]);

  // History for Cmd+Z undo
  const historyRef = useRef<{ nodes: Node[]; edges: Edge[] }[]>([]);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  const pushHistory = useCallback(() => {
    historyRef.current = [
      ...historyRef.current.slice(-49),
      { nodes: nodesRef.current, edges: edgesRef.current },
    ];
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const snapshot = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
  }, [setNodes, setEdges]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        const tag = (document.activeElement as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        undo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo]);

  const onConnect = useCallback(
    (connection: Connection) => {
      pushHistory();
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges, pushHistory],
  );

  const onNodesChangeWithHistory = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      if (changes.some((c) => c.type === 'remove')) pushHistory();
      onNodesChange(changes);
    },
    [onNodesChange, pushHistory],
  );

  const onEdgesChangeWithHistory = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      if (changes.some((c) => c.type === 'remove')) pushHistory();
      onEdgesChange(changes);
    },
    [onEdgesChange, pushHistory],
  );

  const onAddNode = useCallback(
    (kind: NodeKind) => {
      pushHistory();
      const meta = NODE_META[kind];
      const id = crypto.randomUUID();
      const newNode: Node = {
        id,
        type: kind === 'note' ? 'note' : 'diagram',
        position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
        data: { label: kind === 'note' ? '' : meta.label, kind } as DiagramNodeData,
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes, pushHistory],
  );

  // Resume screen — shown before anything else when a saved session exists
  if (!sessionResolved) {
    const savedAt = savedSession ? new Date(savedSession.savedAt) : null;
    return (
      <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] md:h-screen overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8 bg-background">
          <div className="w-14 h-14 rounded-xl primary-gradient flex items-center justify-center">
            <span className="material-symbols-outlined text-on-primary text-2xl">history</span>
          </div>
          <div className="text-center space-y-2 max-w-sm">
            <h2 className="text-lg font-bold text-on-surface">Resume your session?</h2>
            <p className="text-sm text-on-surface-variant">
              You have an unfinished interview from{' '}
              <span className="text-on-surface font-medium">
                {savedAt ? savedAt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'a previous session'}
              </span>
              . You were on stage{' '}
              <span className="text-primary font-medium">{STAGES[savedSession?.stageIndex ?? 0]}</span>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
            <button
              onClick={handleResume}
              className="flex-1 py-2.5 rounded-lg primary-gradient text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Resume
            </button>
            <button
              onClick={handleStartFresh}
              className="flex-1 py-2.5 rounded-lg border border-outline-variant/30 text-on-surface-variant text-sm font-medium hover:text-on-surface hover:border-outline-variant/60 transition-colors"
            >
              Start Fresh
            </button>
          </div>
          <button onClick={() => navigate('/training')} className="text-xs text-on-surface-variant hover:text-on-surface transition-colors">
            ← Back to Training
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] md:h-screen overflow-hidden">
      {/* ── Left: Diagram Canvas (60%) ──────────────────────────────────── */}
      <section className="w-full lg:w-3/5 flex flex-col border-b lg:border-b-0 lg:border-r border-outline-variant/10 min-h-0 max-h-[50vh] lg:max-h-none">
        <div className="flex items-center gap-3 h-10 px-4 bg-surface-container-low border-b border-outline-variant/10 shrink-0">
          <button onClick={() => navigate('/training')} className="text-on-surface-variant flex items-center hover:text-on-surface transition-colors">
            <span className="material-symbols-outlined text-lg">arrow_back</span>
          </button>
          <span className="text-sm font-medium text-on-surface">Architecture Canvas</span>
          <span className="ml-auto text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{interviewType.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}</span>
        </div>
        <DiagramCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeWithHistory}
          onEdgesChange={onEdgesChangeWithHistory}
          onConnect={onConnect}
          onAddNode={onAddNode}
          onNodeDragStart={pushHistory}
        />
      </section>

      {/* ── Right: Chat (40%) ──────────────────────────────────────────── */}
      <section className="w-full lg:w-2/5 h-full flex flex-col overflow-hidden bg-surface-container-lowest">
        <div className="flex items-center gap-2 h-10 px-4 bg-surface-container-low border-b border-outline-variant/10 shrink-0">
          <div className="w-5 h-5 rounded primary-gradient flex items-center justify-center">
            <span className="material-symbols-outlined text-on-primary" style={{ fontSize: 12 }}>auto_awesome</span>
          </div>
          <span className="text-sm font-medium text-on-surface">AI Interviewer</span>
        </div>
        <ChatPanel
          diagramNodes={nodes}
          diagramEdges={edges}
          interviewType={interviewType}
          initialState={activeSession}
          onStateChange={handleStateChange}
          onResetSession={handleResetSession}
        />
      </section>
    </div>
  );
}
